import React, { useState } from 'react';
import { ModelBuilderProvider, useModelBuilder } from '../context/ModelBuilderContext';
import { LayerPalette } from '../components/modelBuilder/LayerPalette';
import { ModelCanvas } from '../components/modelBuilder/ModelCanvas';
import { LayerConfigPanel } from '../components/modelBuilder/LayerConfigPanel';
import { Blob3DView } from '../components/modelBuilder/Blob3DView';
import ModelBrowser from '../components/modelBuilder/ModelBrowser';
import { modelService } from '../services/modelService';
import '../styles/dashboard-pages.css';
import '../styles/modelBuilder.css';
import '../styles/modelBuilder3d.css';

type ViewMode = '2d' | '3d';

const ModelBuilderContent: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    architecture,
    createNewArchitecture,
    layers,
    selectedLayerId,
    getArchitectureJSON,
    reset,
    undo,
    redo,
    history,
    historyIndex,
  } = useModelBuilder();

  const selectedLayer = layers.find(l => l.id === selectedLayerId);

  const handleNewArchitecture = () => {
    const name = prompt('Enter architecture name:', 'My Neural Network');
    if (name) {
      createNewArchitecture(name);
    }
  };

  const handleExportJSON = () => {
    if (!architecture) {
      alert('No architecture to export');
      return;
    }

    const json = getArchitectureJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(architecture.name || 'architecture').replace(/\s+/g, '_')}_${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveModel = async () => {
    if (!architecture || layers.length === 0) {
      alert('No architecture to save. Please create a model first.');
      return;
    }

    const modelName = prompt('Enter model name:', architecture.name || 'My Model');
    if (!modelName) {
      return;
    }

    const description = prompt('Enter model description (optional):', '') || undefined;

    setIsSaving(true);
    try {
      const architectureJson = getArchitectureJSON();
      const architectureObj = JSON.parse(architectureJson);

      await modelService.saveModel({
        name: modelName,
        architecture: architectureObj,
        description,
      });

      alert('Model saved successfully!');
    } catch (error: any) {
      alert(`Failed to save model: ${error.message || 'Unknown error'}`);
      console.error('Error saving model:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadModel = () => {
    setIsBrowserOpen(true);
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="header-content">
          <h1>🧠 Model Builder</h1>
          <p>Design and build neural networks visually with drag-and-drop ease</p>
        </div>
      </div>

      <div className="model-builder-container">
        {/* Left Sidebar - Layer Palette */}
        <aside className="builder-sidebar">
          <LayerPalette />
        </aside>

        {/* Main Canvas */}
        <main className="builder-main">
          <div className="builder-toolbar">
            <div className="toolbar-left">
              <button className="btn-primary" onClick={handleNewArchitecture}>
                ➕ New Architecture
              </button>
              {architecture && (
                <span className="arch-name">{architecture.name}</span>
              )}
            </div>

            <div className="toolbar-center">
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === '2d' ? 'active' : ''}`}
                  onClick={() => setViewMode('2d')}
                  title="2D View - Classic layout"
                >
                  📋 2D
                </button>
                <button
                  className={`view-btn ${viewMode === '3d' ? 'active' : ''}`}
                  onClick={() => setViewMode('3d')}
                  title="3D Blob View - Arcade style"
                >
                  🎮 3D
                </button>
              </div>

              <button
                className="btn-icon"
                onClick={undo}
                disabled={historyIndex <= 0}
                title="Undo"
              >
                ↶
              </button>
              <button
                className="btn-icon"
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                title="Redo"
              >
                ↷
              </button>
            </div>

            <div className="toolbar-right">
              <button className="btn-secondary" onClick={reset}>
                🔄 Reset
              </button>
              <button className="btn-secondary" onClick={handleLoadModel}>
                📂 Load Model
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveModel}
                disabled={isSaving || !architecture || layers.length === 0}
                title={!architecture || layers.length === 0 ? 'Create a model first' : 'Save model to storage'}
              >
                {isSaving ? '💾 Saving...' : '💾 Save Model'}
              </button>
              <button className="btn-primary" onClick={handleExportJSON}>
                📥 Export JSON
              </button>
            </div>
          </div>

          {viewMode === '2d' ? <ModelCanvas /> : <Blob3DView />}
        </main>

        {/* Right Sidebar - Properties Panel */}
        <aside className="builder-properties">
          <LayerConfigPanel layer={selectedLayer || null} />
        </aside>
      </div>

      {/* Model Browser Modal */}
      <ModelBrowser 
        isOpen={isBrowserOpen} 
        onClose={() => setIsBrowserOpen(false)} 
      />
    </div>
  );
};

const ModelBuilder: React.FC = () => {
  return (
    <ModelBuilderProvider>
      <ModelBuilderContent />
    </ModelBuilderProvider>
  );
};

export default ModelBuilder;
